import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { DeleteRequest } from '../types';
import { LoadingSpinner } from './LoadingSpinner';

interface BulkApproveDeleteRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRequests: DeleteRequest[];
  onConfirm: () => Promise<void>;
}

export const BulkApproveDeleteRequestModal: React.FC<BulkApproveDeleteRequestModalProps> = ({
  isOpen,
  onClose,
  selectedRequests,
  onConfirm,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!acknowledged) {
      setError('Please acknowledge that you understand this action is permanent.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onConfirm();
      setAcknowledged(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setAcknowledged(false);
      setError(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Bulk Approve Delete Requests">
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-900 mb-1">Warning: Permanent Action</h4>
            <p className="text-sm text-red-700">
              You are about to permanently delete {selectedRequests.length}{' '}
              {selectedRequests.length === 1 ? 'account' : 'accounts'}. This action cannot be undone.
            </p>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-2">
            Selected Requests ({selectedRequests.length})
          </h4>
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
            {selectedRequests.map((request) => (
              <div
                key={request.requestId}
                className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{request.name}</p>
                    <p className="text-sm text-gray-600">{request.email}</p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {request.accountType === 'user' ? 'Service User' : 'Provider'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Reason: {request.reason}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => {
                setAcknowledged(e.target.checked);
                setError(null);
              }}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={isLoading}
            />
            <span className="text-sm text-gray-700">
              I understand that approving these requests will permanently delete all selected accounts and their associated data. This action cannot be reversed.
            </span>
          </label>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            onClick={handleClose}
            disabled={isLoading}
            className="bg-gray-500 hover:bg-gray-600 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !acknowledged}
            className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Approve All ({selectedRequests.length})
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
